import React, { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import { useGlobalContext } from "context/globalContext";
import { IoMdClose } from "react-icons/io";
import { createOrUpdateActivitiesAPI } from "Api/activitiesAPI";
import PropTypes from "prop-types";

const CreateOrUpdateActivitiesModal = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode,
}) => {
  const {
    activitiesFormData,
    setActivitiesFormData,
    initialActivitiesFormState,
    setFetchActivities,
  } = useGlobalContext();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hasChanged, setHasChanged] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setActivitiesFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setHasChanged(true);
    if (name === "key") {
      setErrorMessage("");
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.height = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.height = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.height = "";
    };
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const token = localStorage.getItem("jwtToken");

    const response = await createOrUpdateActivitiesAPI(
      activitiesFormData,
      setIsSubmitting,
      token
    );
    if (response) {
      const successMessage = activitiesFormData?.id
        ? "Activities updated successfully"
        : "Activities created successfully";
      toast.success(successMessage);

      onSubmit(response.data);
      onClose();
      setActivitiesFormData(initialActivitiesFormState);
      setFetchActivities(true);
    }
    if (!response) {
      toast.error("Error creating/updating Activities. Please try again.");
    }
  };

  const handleCancel = () => {
    setActivitiesFormData(initialActivitiesFormState);
    setHasChanged(false);
    onClose();
  };

  const isFormValid = useMemo(() => {
    return (
      activitiesFormData?.Activity?.trim() &&
      activitiesFormData?.Group?.trim() &&
      activitiesFormData?.Subtitle?.trim() &&
      !errorMessage
    );
  }, [activitiesFormData, errorMessage]);

  if (!isOpen) return null;

  const buttonClass =
    isSubmitting || !isFormValid || !hasChanged
      ? "cursor-not-allowed bg-gray-400"
      : "bg-green-600 hover:bg-green-500";

  const buttonText = isSubmitting
    ? "Submitting..."
    : isEditMode
    ? "Update"
    : "Add";

  return (
    <div className="bg-black fixed inset-0 z-50 flex h-screen items-center justify-center bg-opacity-50">
      <div className="relative z-50 mx-auto flex w-4/5 flex-col items-center justify-start rounded-xl bg-white p-6 shadow-xl dark:!bg-gray-10 dark:text-white">
       <button
                                       type="button"
                                       onClick={handleCancel}
                                        className="absolute top-6 right-6 text-gray-500 hover:text-gray-600 focus:outline-none dark:hover:text-gray-100 "
                                     >
                                       <IoMdClose size={24} />
                                     </button>

        <form onSubmit={handleSubmit} className="w-full">
          <div className="mt-4 flex justify-center gap-4">
            <div className="w-1/2">
              <label
                className="mb-2 block font-bold text-gray-700 dark:text-white"
                htmlFor="Activity"
              >
                <span className="text-red-600">*</span> Activity:
              </label>
              <input
                type="text"
                autoComplete="off"
                name="Activity"
                id="Activity"
                value={activitiesFormData?.Activity}
                onChange={handleChange}
                className="focus:border-transparent h-11 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white "
                required
              />
              {errorMessage && (
                <p className="mt-2 text-sm text-red-500">{errorMessage}</p>
              )}
            </div>
            <div className="w-1/2">
              <label
                className="mb-2 block font-bold text-gray-700 dark:text-white"
                htmlFor="Group"
              >
                <span className="text-red-600">*</span> Group:
              </label>
              <input
                type="text"
                name="Group"
                autoComplete="off"
                id="Group"
                value={activitiesFormData?.Group}
                onChange={handleChange}
                className="focus:border-transparent h-11 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
                required
              />
              {errorMessage && (
                <p className="mt-2 text-sm text-red-500">{errorMessage}</p>
              )}
            </div>
          </div>
          <div className="mt-4 mb-4 flex justify-start gap-4">
            <div className="w-full">
              <label
                className="mb-2 block font-bold text-gray-700 dark:text-white"
                htmlFor="Subtitle"
              >
                <span className="text-red-600">*</span> Subtitle:
              </label>
              <textarea
                name="Subtitle"
                id="Subtitle"
                value={activitiesFormData?.Subtitle}
                onChange={handleChange}
                className="focus:border-transparent min-h-[100px] w-full resize-y rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
                required
              ></textarea>
            </div>
          </div>

          <div className="mb-8 flex justify-end space-x-4">
            <button
              type="submit"
              className={`rounded-md py-2 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${buttonClass}`}
              disabled={isSubmitting || !isFormValid || !hasChanged}
            >
              {buttonText}
            </button>

            <button
              type="button"
              onClick={handleCancel}
              className="rounded-md bg-red-600 py-2 px-4 text-white hover:bg-red-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

CreateOrUpdateActivitiesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool.isRequired,
};

export default CreateOrUpdateActivitiesModal;
